import React, { VFC, useEffect } from 'react';
import { StepEditorProps } from 'src/editor/step-editor';
import { useEditorContext } from 'src/store/editor';
import { apiGetJourney, apiPostJourneyInteractionOpen } from 'src/utils/journeyApi';

import { ContentCreationEditor } from 'src/editor/content-creation/editor';
import { useEditorStore } from 'src/editor/content-creation/editor-store';
import { getJourneyThemeSettings } from 'src/editor/content-creation/helpers/get-journey-theme-settings';
import { useCurrentOrganization } from 'src/store/organization';
import { useFeatureFlag } from 'src/editor/content-creation/feature-flags-store';
import { functionNoop } from 'src/utils/function/noop';
import { journeyToSections } from 'src/editor/content-creation/helpers/transforms/journey-to-sections';
import { getIdFromFriendlySectionPath } from 'src/editor/content-creation/helpers/section/get-id-from-friendly-section-path';
import { usePlayerStore } from 'src/editor/content-creation/neue-player/player-store';
import { useCurrentUser } from 'src/store/user';
import { useTalkToJourneyStore } from 'src/editor/content-creation/talk-to-journey/store';
import { useClipboardStore } from 'src/editor/content-creation/utils/clipboard.store';
import { shallow } from 'zustand/shallow';
import { loadAllFontFamilies } from 'src/document-editor/font-utils';
import { getFirstSection } from 'src/editor/content-creation/helpers/section/find-section-by-id';
import { getAliasAnnotation } from 'src/editor/content-creation/helpers/transforms/get-alias-annotation';
import { converAnnotationToForm } from 'src/common/helpers/annotation';
import JoyrideOnboardingGuide from 'src/onboarding/joyride-onboarding';
import { isCustomFontStrictlyUsedForOrg, useJourneyDynamicFontStore } from 'src/store/journey-dynamic-font';
import { appendDynamicFont, applyStrictCustomFont } from 'src/utils/dynamic-fonts';

interface PageJourneysContentCreationEditProps extends Pick<StepEditorProps, 'nodeId'> {
  // editorTabValue: string;
  // shareTabValue: string;
  uuid: string;
  sectionPath?: string;
}

let originalConsoleLog = console.log;

export const PageJourneysContentCreationEdit: VFC<PageJourneysContentCreationEditProps> = ({ uuid, sectionPath }) => {
  const preventLogs = useFeatureFlag('prevent_logs');

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const initDynamicFonts = useJourneyDynamicFontStore((state) => state.init);
  const currentDynamicFont = useJourneyDynamicFontStore((state) => state.current);
  const isDynamicFontsReady = useJourneyDynamicFontStore((state) => state.isReady);
  const setIsCustomFontStrictlyUsed = useJourneyDynamicFontStore((state) => state.setIsCustomFontStrictlyUsed);

  const journey = useEditorContext((state) => state.journey);
  const setJourney = useEditorContext((state) => state.setJourney);
  const setAnnotation = useEditorContext((state) => state.setAnnotation);
  const resetEditor = useEditorContext((state) => state.resetEditor);
  const setAliasAnnotationNodeId = useEditorContext((state) => state.setAliasAnnotationNodeId);

  const resetNeueEditor = useEditorStore((state) => state.resetEditor);
  const initEditor = useEditorStore((state) => state.initEditor);

  const resetTalkToJourney = useTalkToJourneyStore((state) => state.reset);

  const isInitialized = useClipboardStore((state) => state.isInitialized, shallow);
  const initClipboard = useClipboardStore((state) => state.initClipboard);

  const currentUser = useCurrentUser((state) => state.currentUser);
  const setOrgData = usePlayerStore((state) => state.setOrgData);

  useEffect(() => {
    if (preventLogs) {
      console.log = functionNoop;
    }
    return () => {
      console.log = originalConsoleLog;
    };
  }, [preventLogs]);

  useEffect(() => {
    const onDocumentFocus = async () => {
      await initClipboard();
      window.removeEventListener('focus', onDocumentFocus);
    };

    if (isInitialized) {
      return;
    }
    window.addEventListener('focus', onDocumentFocus);
    return () => {
      window.removeEventListener('focus', onDocumentFocus);
    };
  }, [isInitialized]);

  useEffect(() => {
    const fetchJourney = async (uuid: string) => {
      const jny = await apiGetJourney(uuid);
      setOrgData(jny.organization.slug, jny.creator.uuid === currentUser?.uuid);
      setJourney(jny);

      const brandColor = currentOrganization?.branding?.secondary_color || null;
      const themeSettings = getJourneyThemeSettings(jny.theme, brandColor);
      const sections = journeyToSections(jny);
      const initialSectionShortId = sectionPath ? getIdFromFriendlySectionPath(sectionPath) : null;
      const initialSection = getFirstSection(sections);
      if (initialSection) {
        setAliasAnnotationNodeId(initialSection.aliasAnnotationNodeId || null);
      }
      let initialSectionId = null;
      if (initialSectionShortId) {
        // Validate that the section exists
        const section = sections.find((section) => section.id.startsWith(initialSectionShortId));
        initialSectionId = section ? section.id : null;
      }
      await loadAllFontFamilies();
      await initEditor(
        jny.uuid,
        jny.name,
        journeyToSections(jny),
        themeSettings,
        currentOrganization.feature_flags,
        initialSectionId
      );
      const journeyAnnotation = getAliasAnnotation(jny);
      if (journeyAnnotation && journeyAnnotation.id) {
        setAnnotation(converAnnotationToForm(journeyAnnotation));
      }
      // await initClipboard();
      apiPostJourneyInteractionOpen(uuid);
      const dynamicFonts = {
        headings: {
          font_family: jny.theme.dynamic_font_preferences?.headings?.font_family || 'CircularXX',
          font_variant: jny.theme.dynamic_font_preferences?.headings?.font_variant || 'regular',
        },
        paragraphs: {
          font_family: jny.theme.dynamic_font_preferences?.paragraphs?.font_family || 'CircularXX',
          font_variant: jny.theme.dynamic_font_preferences?.paragraphs?.font_variant || 'regular',
        },
      };

      return dynamicFonts;
    };

    if (uuid) {
      fetchJourney(uuid)
        .then(initDynamicFonts)
        .then(() => {
          if (!currentOrganization) {
            return;
          }
          const loadCustomFont = isCustomFontStrictlyUsedForOrg(currentOrganization.slug);
          setIsCustomFontStrictlyUsed(loadCustomFont);
          applyStrictCustomFont({ variant: 'regular' });
        });
    }
    // empty out journey to avoid flicker if the user loads up another journey
    return () => {
      resetEditor();
      resetNeueEditor();
      resetTalkToJourney();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, currentUser]);

  useEffect(() => {
    if (isDynamicFontsReady) {
      appendDynamicFont('headings', currentDynamicFont.headings);
      appendDynamicFont('paragraphs', currentDynamicFont.paragraphs);
    }
  }, [isDynamicFontsReady, currentDynamicFont]);

  if (!journey) {
    return null;
  }

  return (
    <>
      <ContentCreationEditor key={uuid} />
      <JoyrideOnboardingGuide initial={true} journey={journey} />
    </>
  );
};
